import type { PlatformTypeName } from "@multi-chat/shared";

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;

/** Same 24h customer-service window the API enforces for WhatsApp, Messenger and Instagram. */
export const REPLY_WINDOW_MS = 24 * MS_PER_HOUR;

export type ReplyWindow =
  | { kind: "OPEN"; remainingMs: number; expiresAtMs: number; lastInboundAtMs: number | null }
  | {
      kind: "CLOSED";
      expiresAtMs: number | null;
      lastInboundAtMs: number | null;
      /** WhatsApp can still reach the customer with an approved template. */
      templateRequired: boolean;
    }
  | { kind: "NONE" };

export type ReplyWindowTone = "open" | "warn" | "closed";

/** Remaining window units (the window is at most 24h). */
export type ReplyWindowRemainingUnit = "hour" | "minute";

/**
 * Reply-window state for every channel. WhatsApp uses the API's window expiry; Messenger and
 * Instagram derive it from the last customer message (same 24h rule) and defer to the backend
 * when it reports free-form sending as closed.
 */
export function deriveReplyWindow(input: {
  platformType: PlatformTypeName;
  customerServiceWindowExpiresAt: string | null;
  lastInboundCustomerMessageAt: string | null;
  nowMs: number;
  /** Backend-authoritative closed window for Messenger and Instagram. */
  metaWindowClosed?: boolean;
}): ReplyWindow {
  const { platformType, nowMs } = input;
  const lastInboundAtMs = parseIsoMs(input.lastInboundCustomerMessageAt);

  switch (platformType) {
    case "WHATSAPP": {
      const expiresAtMs = parseIsoMs(input.customerServiceWindowExpiresAt);
      if (expiresAtMs == null) return { kind: "NONE" };
      if (expiresAtMs > nowMs) {
        return { kind: "OPEN", remainingMs: expiresAtMs - nowMs, expiresAtMs, lastInboundAtMs };
      }
      return { kind: "CLOSED", expiresAtMs, lastInboundAtMs, templateRequired: true };
    }
    case "FACEBOOK":
    case "INSTAGRAM": {
      const expiresAtMs = lastInboundAtMs == null ? null : lastInboundAtMs + REPLY_WINDOW_MS;
      if (input.metaWindowClosed || (expiresAtMs != null && expiresAtMs <= nowMs)) {
        return { kind: "CLOSED", expiresAtMs, lastInboundAtMs, templateRequired: false };
      }
      if (expiresAtMs == null) return { kind: "NONE" };
      return { kind: "OPEN", remainingMs: expiresAtMs - nowMs, expiresAtMs, lastInboundAtMs };
    }
    default: {
      const _exhaustive: never = platformType;
      return _exhaustive;
    }
  }
}

/** Under one hour left reads as a warning. */
export function replyWindowTone(window: Exclude<ReplyWindow, { kind: "NONE" }>): ReplyWindowTone {
  switch (window.kind) {
    case "OPEN":
      return window.remainingMs < MS_PER_HOUR ? "warn" : "open";
    case "CLOSED":
      return "closed";
    default: {
      const _exhaustive: never = window;
      return _exhaustive;
    }
  }
}

/** Human-readable remaining unit/count for an open window (no seconds). */
export function remainingReplyWindowParts(
  remainingMs: number,
): { unit: ReplyWindowRemainingUnit; count: number } {
  if (remainingMs >= MS_PER_HOUR) {
    return {
      unit: "hour",
      count: Math.max(1, Math.floor(remainingMs / MS_PER_HOUR)),
    };
  }
  return {
    unit: "minute",
    count: Math.max(1, Math.ceil(remainingMs / MS_PER_MINUTE)),
  };
}

function parseIsoMs(value: string | null): number | null {
  if (value == null || value.trim() === "") return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Secondary identity when it differs from the primary title (phone preferred over username).
 * Instagram handles read as `@handle`, matching Details and the contact lookup.
 */
export function conversationHeaderSecondaryIdentity(input: {
  title: string;
  platformType?: PlatformTypeName;
  normalizedPhoneNumber: string | null | undefined;
  platformUsername: string | null | undefined;
}): string | null {
  const phone = input.normalizedPhoneNumber?.trim() || null;
  const username = input.platformUsername?.trim().replace(/^@/, "") || null;
  if (phone && differsFromTitle(phone, input.title)) return phone;
  if (username && differsFromTitle(username, input.title.replace(/^@/, ""))) {
    return input.platformType === "INSTAGRAM" ? `@${username}` : username;
  }
  return null;
}

function differsFromTitle(value: string, title: string): boolean {
  return value.trim().toLowerCase() !== title.trim().toLowerCase();
}
